import { useEffect, useState } from 'react';
import { CreditCard, Plus, Pencil, Trash2, CheckCircle, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

const RULE_ICONS = {
  upfront_full:    { color: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/30',  label: 'Full Upfront',       desc: 'Full payment required before production starts' },
  deposit_percent: { color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/30',   label: 'Deposit + Balance',  desc: 'Percentage deposit now, remainder before ship' },
  invoice_first:   { color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', label: 'Invoice First',      desc: 'Invoice issued, payment due before production' },
  net_terms:       { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', label: 'Net Terms',          desc: 'Net-30 / Net-60 — pay after delivery' },
  manual_review:   { color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', label: 'Manual Review',      desc: 'Admin approves payment method per order' },
};

function RuleForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: '', rule_type: 'upfront_full', deposit_percent: '', net_days: 30, credit_limit: '', notes: '', active: true,
    ...initial,
  });
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const inputCls = "w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2.5 outline-none transition-colors placeholder-zinc-600";

  return (
    <div className="bg-[#1a1a1a] border border-orange-500/30 p-5 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Rule Name *</label>
          <input value={form.name} onChange={set('name')} className={inputCls} placeholder="e.g. Standard Net-30, VIP Upfront" />
        </div>
        <div className="col-span-2">
          <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Payment Type *</label>
          <select value={form.rule_type} onChange={set('rule_type')} className={inputCls}>
            {Object.entries(RULE_ICONS).map(([k, v]) => (
              <option key={k} value={k}>{v.label} — {v.desc}</option>
            ))}
          </select>
        </div>
        {form.rule_type === 'deposit_percent' && (
          <div>
            <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Deposit % *</label>
            <input type="number" min="1" max="99" value={form.deposit_percent} onChange={set('deposit_percent')} className={inputCls} placeholder="50" />
            <p className="font-inter text-[9px] text-zinc-600 mt-1">% due at order. Remainder due before shipment.</p>
          </div>
        )}
        {form.rule_type === 'net_terms' && (
          <div>
            <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Net Days *</label>
            <input type="number" min="1" value={form.net_days} onChange={set('net_days')} className={inputCls} placeholder="30" />
            <p className="font-inter text-[9px] text-zinc-600 mt-1">Days after delivery to pay invoice.</p>
          </div>
        )}
        <div>
          <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Credit Limit ($)</label>
          <input type="number" value={form.credit_limit} onChange={set('credit_limit')} className={inputCls} placeholder="Optional — leave blank for no limit" />
        </div>
        <div className="col-span-2">
          <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Admin Notes</label>
          <input value={form.notes} onChange={set('notes')} className={inputCls} placeholder="Internal note about this rule" />
        </div>
        <div className="col-span-2 flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setForm(p => ({ ...p, active: !p.active }))}
              className={`w-9 h-5 rounded-full relative transition-colors cursor-pointer ${form.active ? 'bg-orange-500' : 'bg-zinc-700'}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.active ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </div>
            <span className="font-inter text-xs text-zinc-400">{form.active ? 'Active' : 'Inactive'}</span>
          </label>
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <button onClick={() => onSave(form)}
          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-base tracking-widest py-2.5 transition-colors">
          {initial ? 'SAVE CHANGES' : 'CREATE RULE'}
        </button>
        <button onClick={onCancel} className="flex-1 border border-white/20 text-zinc-400 hover:text-white font-bebas text-base tracking-widest py-2.5 transition-colors">
          CANCEL
        </button>
      </div>
    </div>
  );
}

export default function AdminPaymentRules() {
  const { token } = useAdminAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await fetch(`${BASE}/api/admin/payment-rules`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRules(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [token]);

  const handleCreate = async (form) => {
    if (!form.name || !form.rule_type) { setError('Name and rule type are required'); return; }
    try {
      const res = await fetch(`${BASE}/api/admin/payment-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, deposit_percent: form.deposit_percent ? +form.deposit_percent : null, net_days: form.net_days ? +form.net_days : null, credit_limit: form.credit_limit ? +form.credit_limit : null }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      setShowCreate(false);
      setError('');
      load();
    } catch (e) { setError(e.message); }
  };

  const handleUpdate = async (ruleId, form) => {
    try {
      const res = await fetch(`${BASE}/api/admin/payment-rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: form.name, deposit_percent: form.deposit_percent ? +form.deposit_percent : null, net_days: form.net_days ? +form.net_days : null, credit_limit: form.credit_limit ? +form.credit_limit : null, notes: form.notes, active: form.active }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      setEditRule(null);
      load();
    } catch (e) { setError(e.message); }
  };

  const handleDelete = async (ruleId, name) => {
    if (!window.confirm(`Delete rule "${name}"? Dealers assigned this rule will need a new one.`)) return;
    await fetch(`${BASE}/api/admin/payment-rules/${ruleId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const rulesByType = Object.keys(RULE_ICONS);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-bebas text-3xl text-white tracking-wider flex items-center gap-3">
            <CreditCard size={22} className="text-orange-500" />PAYMENT RULES
          </h1>
          <p className="font-inter text-xs text-zinc-500 mt-1">
            Define payment terms per dealer. Assign rules from the Dealer Profile page.
          </p>
        </div>
        <button onClick={() => { setShowCreate(true); setEditRule(null); }}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-base tracking-widest px-5 py-2.5 transition-colors">
          <Plus size={16} />NEW RULE
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3">{error}</div>}

      {/* Rule type explainer */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        {rulesByType.map(k => {
          const cfg = RULE_ICONS[k];
          const count = rules.filter(r => r.rule_type === k).length;
          return (
            <div key={k} className={`${cfg.bg} ${cfg.border} border p-3`}>
              <p className={`font-bebas text-sm ${cfg.color} tracking-wider`}>{cfg.label}</p>
              <p className="font-inter text-[9px] text-zinc-600 mt-0.5 leading-tight">{cfg.desc}</p>
              <p className={`font-inter text-[10px] mt-2 ${cfg.color}`}>{count} rule{count !== 1 ? 's' : ''}</p>
            </div>
          );
        })}
      </div>

      {showCreate && !editRule && (
        <RuleForm onSave={handleCreate} onCancel={() => setShowCreate(false)} />
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : rules.length === 0 ? (
        <div className="text-center py-14 bg-[#1a1a1a] border border-white/10">
          <CreditCard size={32} className="text-zinc-700 mx-auto mb-3" />
          <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO PAYMENT RULES YET</p>
          <p className="font-inter text-sm text-zinc-600 mt-1 mb-4">Create your first payment rule to assign to dealers</p>
          <button onClick={() => setShowCreate(true)}
            className="font-bebas text-base tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 transition-colors">
            + CREATE FIRST RULE
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => {
            const cfg = RULE_ICONS[rule.rule_type] || RULE_ICONS.manual_review;
            const isEditing = editRule === rule.rule_id;
            return (
              <div key={rule.rule_id} className={`bg-[#1a1a1a] border ${rule.active ? cfg.border : 'border-zinc-800'} overflow-hidden`}>
                {isEditing ? (
                  <RuleForm
                    initial={rule}
                    onSave={(form) => handleUpdate(rule.rule_id, form)}
                    onCancel={() => setEditRule(null)}
                  />
                ) : (
                  <div className="flex items-center justify-between px-5 py-4 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`flex-shrink-0 w-2 h-2 rounded-full ${rule.active ? 'bg-orange-500' : 'bg-zinc-700'}`} />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-inter text-sm font-semibold text-white">{rule.name}</span>
                          <span className={`font-inter text-[9px] font-bold px-2 py-0.5 border uppercase tracking-wider ${cfg.color} ${cfg.bg} ${cfg.border}`}>
                            {cfg.label}
                          </span>
                          {!rule.active && <span className="font-inter text-[9px] text-zinc-600 border border-zinc-700 px-1.5 py-0.5">INACTIVE</span>}
                        </div>
                        <div className="flex items-center gap-4 mt-0.5 flex-wrap">
                          <span className="font-inter text-xs text-zinc-500">{cfg.desc}</span>
                          {rule.deposit_percent && <span className="font-inter text-[10px] text-blue-400">Deposit: {rule.deposit_percent}%</span>}
                          {rule.net_days && <span className="font-inter text-[10px] text-orange-400">Net-{rule.net_days}</span>}
                          {rule.credit_limit && <span className="font-inter text-[10px] text-zinc-500">Limit: ${rule.credit_limit.toLocaleString()}</span>}
                          {rule.notes && <span className="font-inter text-[10px] text-zinc-600">{rule.notes}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditRule(rule.rule_id); setShowCreate(false); }}
                        className="flex items-center gap-1.5 font-inter text-xs text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 transition-colors">
                        <Pencil size={12} />Edit
                      </button>
                      <button onClick={() => handleDelete(rule.rule_id, rule.name)}
                        className="flex items-center gap-1.5 font-inter text-xs text-zinc-600 hover:text-red-400 border border-white/5 hover:border-red-500/30 px-3 py-1.5 transition-colors">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Authorize.Net status */}
      <div className="bg-[#1a1a1a] border border-white/10 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield size={15} className="text-green-500" />
            <div>
              <p className="font-bebas text-base text-white tracking-wider">AUTHORIZE.NET — CONNECTED</p>
              <p className="font-inter text-xs text-zinc-500">
                Sandbox mode · API Login: 7Zdp45UXVrk · Ready to charge cards
              </p>
            </div>
          </div>
          <span className="font-inter text-[10px] text-green-400 border border-green-500/30 bg-green-500/10 px-2 py-1 uppercase tracking-wider">
            Sandbox Active
          </span>
        </div>
      </div>
    </div>
  );
}
